// src/redux/connectionSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getCookie } from '../utils/cookies';
import { logoutUser } from './auth';

const API_BASE = process.env.REACT_APP_API_URL;

// ───────── Thunks ─────────

// Fetch all accepted connections
export const fetchConnections = createAsyncThunk(
  'connections/fetchConnections',
  async (_, thunkAPI) => {
    const token = thunkAPI.getState().auth.user?.token;
    const res = await fetch(`${API_BASE}/api/connections/list`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': getCookie('XSRF-TOKEN'),
        Authorization: token ? `Bearer ${token}` : undefined
      }
    });
    const data = await res.json();
    if (!res.ok) return thunkAPI.rejectWithValue(data.error || 'Fetch failed');
    return data;
  }
);

// Fetch pending incoming requests
export const fetchIncomingRequests = createAsyncThunk(
  'connections/fetchIncomingRequests',
  async (_, thunkAPI) => {
    const token = thunkAPI.getState().auth.user?.token;
    const res = await fetch(`${API_BASE}/api/connections/requests/incoming`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': getCookie('XSRF-TOKEN'),
        Authorization: token ? `Bearer ${token}` : undefined
      }
    });
    const data = await res.json();
    if (!res.ok) return thunkAPI.rejectWithValue(data.error || 'Fetch failed');
    return data;
  }
);

// Fetch pending outgoing requests
export const fetchOutgoingRequests = createAsyncThunk(
  'connections/fetchOutgoingRequests',
  async (_, thunkAPI) => {
    const token = thunkAPI.getState().auth.user?.token;
    const res = await fetch(`${API_BASE}/api/connections/requests/outgoing`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': getCookie('XSRF-TOKEN'),
        Authorization: token ? `Bearer ${token}` : undefined
      }
    });
    const data = await res.json();
    if (!res.ok) return thunkAPI.rejectWithValue(data.error || 'Fetch failed');
    return data;
  }
);

// Fetch connection counts for badges
export const fetchConnectionCounts = createAsyncThunk(
  'connections/fetchCounts',
  async (_, thunkAPI) => {
    const token = thunkAPI.getState().auth.user?.token;
    const res = await fetch(`${API_BASE}/api/connections/count`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': getCookie('XSRF-TOKEN'),
        Authorization: token ? `Bearer ${token}` : undefined
      }
    });
    const data = await res.json();
    if (!res.ok) return thunkAPI.rejectWithValue(data.error || 'Fetch failed');
    return data;
  }
);

// Send connection request
export const sendConnectionRequest = createAsyncThunk(
  'connections/sendRequest',
  async (userName, thunkAPI) => {
    const token = thunkAPI.getState().auth.user?.token;
    const res = await fetch(`${API_BASE}/api/connections/request/${userName}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': getCookie('XSRF-TOKEN'),
        Authorization: token ? `Bearer ${token}` : undefined
      }
    });
    const data = await res.json();
    if (!res.ok) return thunkAPI.rejectWithValue(data.error || 'Request failed');
    return { ...data, userName };
  }
);

// Scan QR code to find user
export const scanQRCode = createAsyncThunk(
  'connections/scanQRCode',
  async (qrCode, thunkAPI) => {
    const token = thunkAPI.getState().auth.user?.token;
    const res = await fetch(`${API_BASE}/api/connections/scan/${encodeURIComponent(qrCode)}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': getCookie('XSRF-TOKEN'),
        Authorization: token ? `Bearer ${token}` : undefined
      }
    });
    const data = await res.json();
    if (!res.ok) return thunkAPI.rejectWithValue(data.error || 'Scan failed');
    return data;
  }
);

// Accept connection request
export const acceptConnectionRequest = createAsyncThunk(
  'connections/acceptRequest',
  async (connectionId, thunkAPI) => {
    const token = thunkAPI.getState().auth.user?.token;
    const res = await fetch(`${API_BASE}/api/connections/accept/${connectionId}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': getCookie('XSRF-TOKEN'),
        Authorization: token ? `Bearer ${token}` : undefined
      }
    });
    const data = await res.json();
    if (!res.ok) return thunkAPI.rejectWithValue(data.error || 'Accept failed');
    return { ...data, connectionId };
  }
);

// Reject connection request
export const rejectConnectionRequest = createAsyncThunk(
  'connections/rejectRequest',
  async (connectionId, thunkAPI) => {
    const token = thunkAPI.getState().auth.user?.token;
    const res = await fetch(`${API_BASE}/api/connections/reject/${connectionId}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': getCookie('XSRF-TOKEN'),
        Authorization: token ? `Bearer ${token}` : undefined
      }
    });
    const data = await res.json();
    if (!res.ok) return thunkAPI.rejectWithValue(data.error || 'Reject failed');
    return { ...data, connectionId };
  }
);

// Remove connection
export const removeConnection = createAsyncThunk(
  'connections/remove',
  async (connectionId, thunkAPI) => {
    const token = thunkAPI.getState().auth.user?.token;
    const res = await fetch(`${API_BASE}/api/connections/${connectionId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': getCookie('XSRF-TOKEN'),
        Authorization: token ? `Bearer ${token}` : undefined
      }
    });
    const data = await res.json();
    if (!res.ok) return thunkAPI.rejectWithValue(data.error || 'Remove failed');
    return { connectionId };
  }
);

// Search users
export const searchUsers = createAsyncThunk(
  'connections/searchUsers',
  async (query, thunkAPI) => {
    const token = thunkAPI.getState().auth.user?.token;
    const res = await fetch(`${API_BASE}/api/tripper/search?q=${encodeURIComponent(query)}`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': getCookie('XSRF-TOKEN'),
        Authorization: token ? `Bearer ${token}` : undefined
      }
    });
    const data = await res.json();
    if (!res.ok) return thunkAPI.rejectWithValue(data.error || 'Search failed');
    return data;
  }
);

// Fetch public profile
export const fetchPublicProfile = createAsyncThunk(
  'connections/fetchPublicProfile',
  async (userName, thunkAPI) => {
    const token = thunkAPI.getState().auth.user?.token;
    const res = await fetch(`${API_BASE}/api/tripper/user/${userName}`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': getCookie('XSRF-TOKEN'),
        Authorization: token ? `Bearer ${token}` : undefined
      }
    });
    const data = await res.json();
    if (!res.ok) return thunkAPI.rejectWithValue(data.error || 'Fetch failed');
    return data;
  }
);

// ───────── Slice ─────────

const initialState = {
  connections: [],
  incomingRequests: [],
  outgoingRequests: [],
  searchResults: [],
  publicProfile: null,
  scannedUser: null,
  scanStatus: 'idle',
  counts: { pendingIncoming: 0, totalConnections: 0 },
  status: 'idle',
  searchStatus: 'idle',
  profileStatus: 'idle',
  error: null
};

const connectionSlice = createSlice({
  name: 'connections',
  initialState,
  reducers: {
    clearSearchResults(state) {
      state.searchResults = [];
      state.searchStatus = 'idle';
    },
    clearPublicProfile(state) {
      state.publicProfile = null;
      state.profileStatus = 'idle';
    },
    clearScannedUser(state) {
      state.scannedUser = null;
      state.scanStatus = 'idle';
    },
    clearConnectionError(state) {
      state.error = null;
    }
  },
  extraReducers: builder => {
    builder
      // Fetch connections
      .addCase(fetchConnections.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchConnections.fulfilled, (state, { payload }) => {
        state.status = 'succeeded';
        state.connections = payload;
      })
      .addCase(fetchConnections.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Fetch incoming requests
      .addCase(fetchIncomingRequests.pending, state => {
        state.status = 'loading';
      })
      .addCase(fetchIncomingRequests.fulfilled, (state, { payload }) => {
        state.status = 'succeeded';
        state.incomingRequests = payload;
      })
      .addCase(fetchIncomingRequests.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Fetch outgoing requests
      .addCase(fetchOutgoingRequests.pending, state => {
        state.status = 'loading';
      })
      .addCase(fetchOutgoingRequests.fulfilled, (state, { payload }) => {
        state.status = 'succeeded';
        state.outgoingRequests = payload;
      })
      .addCase(fetchOutgoingRequests.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Fetch counts
      .addCase(fetchConnectionCounts.fulfilled, (state, { payload }) => {
        state.counts = payload;
      })

      // Scan QR code
      .addCase(scanQRCode.pending, state => {
        state.scanStatus = 'loading';
        state.scannedUser = null;
        state.error = null;
      })
      .addCase(scanQRCode.fulfilled, (state, { payload }) => {
        state.scanStatus = 'succeeded';
        state.scannedUser = payload;
      })
      .addCase(scanQRCode.rejected, (state, action) => {
        state.scanStatus = 'failed';
        state.scannedUser = null;
        state.error = action.payload;
      })

      // Send request
      .addCase(sendConnectionRequest.fulfilled, (state, { payload }) => {
        const newStatus = payload.connection?.status || 'pending';
        // Update search results to reflect new status
        state.searchResults = state.searchResults.map(user =>
          user.userName === payload.userName
            ? { ...user, connection: { status: newStatus, connectionId: payload.connection?._id, isRequester: true } }
            : user
        );
        // Update public profile if viewing
        if (state.publicProfile?.userName === payload.userName) {
          state.publicProfile.connection = {
            status: newStatus,
            connectionId: payload.connection?._id,
            isRequester: true
          };
        }
        // Update the QR-scanned user overlay so it reflects the request we just
        // sent (previously this was refetched via scanQRCode, which failed
        // because the scan response never includes the target's qrCode).
        if (state.scannedUser?.user?.userName === payload.userName) {
          state.scannedUser.connectionStatus = newStatus;
          state.scannedUser.connectionId = payload.connection?._id || null;
          state.scannedUser.isRequester = true;
        }
      })

      // Accept request
      .addCase(acceptConnectionRequest.fulfilled, (state, { payload }) => {
        state.incomingRequests = state.incomingRequests.filter(
          req => req._id !== payload.connectionId
        );
        state.counts.pendingIncoming = Math.max(0, state.counts.pendingIncoming - 1);
        state.counts.totalConnections += 1;
      })

      // Reject request
      .addCase(rejectConnectionRequest.fulfilled, (state, { payload }) => {
        state.incomingRequests = state.incomingRequests.filter(
          req => req._id !== payload.connectionId
        );
        state.counts.pendingIncoming = Math.max(0, state.counts.pendingIncoming - 1);
      })

      // Remove connection
      .addCase(removeConnection.fulfilled, (state, { payload }) => {
        state.connections = state.connections.filter(
          conn => conn._id !== payload.connectionId
        );
        state.outgoingRequests = state.outgoingRequests.filter(
          req => req._id !== payload.connectionId
        );
        state.counts.totalConnections = Math.max(0, state.counts.totalConnections - 1);
      })

      // Search users
      .addCase(searchUsers.pending, state => {
        state.searchStatus = 'loading';
      })
      .addCase(searchUsers.fulfilled, (state, { payload }) => {
        state.searchStatus = 'succeeded';
        state.searchResults = payload;
      })
      .addCase(searchUsers.rejected, (state, action) => {
        state.searchStatus = 'failed';
        state.error = action.payload;
      })

      // Fetch public profile
      .addCase(fetchPublicProfile.pending, state => {
        state.profileStatus = 'loading';
        state.publicProfile = null;
      })
      .addCase(fetchPublicProfile.fulfilled, (state, { payload }) => {
        state.profileStatus = 'succeeded';
        state.publicProfile = payload;
      })
      .addCase(fetchPublicProfile.rejected, (state, action) => {
        state.profileStatus = 'failed';
        state.error = action.payload;
      })

      // Logout - reset state
      .addCase(logoutUser.fulfilled, () => initialState);
  }
});

export const { clearSearchResults, clearPublicProfile, clearScannedUser, clearConnectionError } = connectionSlice.actions;
export default connectionSlice.reducer;
